import { useEffect, useRef, type MutableRefObject } from 'react';
import type { SmriqContext, SmriqPlanSettings } from '../types/smriq.types';

interface UseSmriqAutoGenerateArgs {
    settingsRef: MutableRefObject<SmriqPlanSettings | null | undefined>;
    context: SmriqContext | undefined;
    confirmed: boolean;
    needsReconfirm: boolean;
    isGenerating: boolean;
    autoGenerateReady: boolean;
    planFieldsEdited: boolean;
    hasPlan: boolean;
    generate: (payload: SmriqPlanSettings) => void;
    /** A new value (a reconfirm's fresh vehicle snapshot) re-arms the one-shot generate. */
    rearmKey: unknown;
}

/** With a duration and an MOT setting known, generates once per session with no plan on screen
 *  (on load or after a reconfirm); saved quote settings win over product defaults. */
export function useSmriqAutoGenerate({
    settingsRef,
    context,
    confirmed,
    needsReconfirm,
    isGenerating,
    autoGenerateReady,
    planFieldsEdited,
    hasPlan,
    generate,
    rearmKey,
}: UseSmriqAutoGenerateArgs) {
    const generatedRef = useRef(false);
    const armedForRef = useRef(rearmKey);

    useEffect(() => {
        if (armedForRef.current !== rearmKey) {
            armedForRef.current = rearmKey;
            generatedRef.current = false;
        }
        if (generatedRef.current || planFieldsEdited || hasPlan) return;
        if (!confirmed || needsReconfirm || !context || isGenerating || !autoGenerateReady) return;
        const settings = settingsRef.current;
        const duration = settings?.duration_months ?? context.duration.default;
        const includeMot = settings?.include_mot_tests ?? context.mot.default;
        if (duration == null || includeMot == null) return;
        generatedRef.current = true;
        generate({ duration_months: duration, include_mot_tests: includeMot });
    }, [
        settingsRef,
        confirmed,
        needsReconfirm,
        context,
        isGenerating,
        autoGenerateReady,
        planFieldsEdited,
        hasPlan,
        generate,
        rearmKey,
    ]);
}
